import useAuth from "../../../hooks/useAuth";
import { useEffect, useState } from "react";
import { axiosPrivate } from "../../../api/axios";
import { useTranslation } from "react-i18next";

// Components
import LoadingSpinner from "../../loadingSpinner/LoadingSpinner";

// Styling
import "../../../css/AdminOverride.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

export default function AdminOverride({ isNavOpen }) {
  const { auth } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [school, setSchool] = useState(null);
  const schoolId = school?.school_id;

  const [overrides, setOverrides] = useState([]);

  // NEW: Track plan + field being edited
  const [editing, setEditing] = useState({ planId: null, field: null });

  const [fieldError, setFieldError] = useState({
    planId: null,
    field: null,
    message: "",
  });

  const [editedValues, setEditedValues] = useState({
    price: "",
    number_of_levels: "",
    number_of_users: "",
    storage_mb: "",
  });

  // -----------------------------
  // FETCH SCHOOL
  // -----------------------------
  const fetchSchoolData = async () => {
    if (!backendUrl) return;
    setLoading(true);
    try {
      const response = await axiosPrivate.get("/school/admin");
      setSchool(response.data[0] || null);
    } catch (err) {
      console.error("Error fetching school data:", err);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // FETCH OVERRIDES
  // -----------------------------
  const fetchOverrides = async (schoolId) => {
    if (!schoolId) return;
    try {
      const res = await axiosPrivate.get(
        `/admin/subscription/override?school_id=${schoolId}`
      );
      setOverrides(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err.response?.status === 404) {
        setOverrides([]);
      } else {
        console.error("Failed to fetch override:", err);
      }
    }
  };

  // -----------------------------
  // PLAN NAME TRANSLATION
  // -----------------------------
  const getPlanNameFromId = (id) => {
    const map = { 1: "starter", 2: "essential", 3: "pro" };
    const key = map[id];
    return key ? t(`adminSubscription.subscription.${key}.name`) : "";
  };

  // -----------------------------
  // START EDITING A FIELD
  // -----------------------------
  const startEditingField = (row, fieldName) => {
    setEditing({ planId: row.override_for_plan_id, field: fieldName });
    setEditedValues({
      price: row.price,
      number_of_levels: row.number_of_levels,
      number_of_users: row.number_of_users,
      storage_mb: row.storage_mb,
    });
  };

  // -----------------------------
  // SAVE FIELD TO BACKEND
  // -----------------------------
  const saveFieldToBackend = async (fieldName, value) => {
    try {
      const payload = {
        school_id: schoolId,
        override_for_plan_id: editing.planId,
        [fieldName]: value,
      };

      await axiosPrivate.put(
        "/admin/subscription/override/update-field",
        payload
      );

      await fetchOverrides(schoolId);

      // ✅ Clear editing + errors on success
      setEditing({ planId: null, field: null });
      setFieldError({ planId: null, field: null, message: "" });

    } catch (err) {
      const message =
        err.response?.data?.error ||
        t("common.errors.generic");

      setFieldError({
        planId: editing.planId,
        field: fieldName,
        message,
      });
    }
  };




  // -----------------------------
  // EFFECTS
  // -----------------------------
  useEffect(() => {
    if (!auth?.roles) return;
    fetchSchoolData();
  }, [auth?.roles]);

  useEffect(() => {
    if (schoolId) fetchOverrides(schoolId);
  }, [schoolId]);

  if (!auth?.roles) return null;

  // Helper: Check if a field is being edited
  const isEditingField = (row, field) =>
    editing.planId === row.override_for_plan_id &&
    editing.field === field;

  // -----------------------------
  // JSX
  // -----------------------------
  return (
    <div className={`${isNavOpen ? "body-squeezed" : "body"}`}>
      <h2>Admin Override Subscription</h2>

      {loading && (
        <div style={{ marginTop: "2rem" }}>
          <LoadingSpinner />
        </div>
      )}

      {!loading && overrides.length > 0 && (
        <div className="override-cards-container">
          {overrides.map((row) => (
            <div key={row.override_for_plan_id} className="override-card">
              <h3>{getPlanNameFromId(row.override_for_plan_id)}</h3>

              {/* ------------------- PRICE ------------------- */}
              <div className="override-field">
                <label>Price (CAD)</label>

                {isEditingField(row, "price") &&
                  fieldError.planId === row.override_for_plan_id &&
                  fieldError.field === "price" && (
                    <div className="override-error">
                      {fieldError.message}
                    </div>
                  )}

                {isEditingField(row, "price") ? (
                  <div className="edit-row">
                    <input
                      type="number"
                      value={editedValues.price}
                      onChange={(e) => {
                        setEditedValues({
                          ...editedValues,
                          price: e.target.value,
                        });

                        setFieldError({ planId: null, field: null, message: "" });
                      }}
                    />
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="icon-save"
                      onClick={() =>
                        saveFieldToBackend("price", editedValues.price)
                      }
                    />
                    <FontAwesomeIcon
                      icon={faTimes}
                      className="icon-cancel"
                      onClick={() => {
                        setEditing({ planId: null, field: null });
                        setFieldError({ planId: null, field: null, message: "" });
                      }}
                    />
                  </div>
                ) : (
                  <div className="value-row">
                    {row.price}
                    <FontAwesomeIcon
                      icon={faEdit}
                      className="icon-edit"
                      onClick={() => startEditingField(row, "price")}
                    />
                  </div>
                )}
              </div>

              {/* ------------------- LEVELS ------------------- */}
              <div className="override-field">
                <label>Levels</label>

                {isEditingField(row, "number_of_levels") ? (
                  <div className="edit-row">
                    <input
                      type="number"
                      value={editedValues.number_of_levels}


                      onChange={(e) => {
                        setEditedValues({
                          ...editedValues,
                          number_of_levels: e.target.value,
                        });

                        setFieldError({ planId: null, field: null, message: "" });
                      }}


                    />
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="icon-save"
                      onClick={() =>
                        saveFieldToBackend(
                          "number_of_levels",
                          editedValues.number_of_levels
                        )
                      }
                    />
                    <FontAwesomeIcon
                      icon={faTimes}
                      className="icon-cancel"
                      onClick={() => {
                        setEditing({ planId: null, field: null });
                        setFieldError({ planId: null, field: null, message: "" });
                      }}
                    />
                  </div>
                ) : (
                  <div className="value-row">
                    {row.number_of_levels}
                    <FontAwesomeIcon
                      icon={faEdit}
                      className="icon-edit"
                      onClick={() => startEditingField(row, "number_of_levels")}
                    />
                  </div>
                )}
              </div>

              {/* ------------------- USERS ------------------- */}
              <div className="override-field">
                <label>Users</label>

                {isEditingField(row, "number_of_users") ? (
                  <div className="edit-row">
                    <input
                      type="number"
                      value={editedValues.number_of_users}


                      onChange={(e) => {
                        setEditedValues({
                          ...editedValues,
                          number_of_users: e.target.value,
                        });

                        setFieldError({ planId: null, field: null, message: "" });
                      }}


                    />
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="icon-save"
                      onClick={() =>
                        saveFieldToBackend(
                          "number_of_users",
                          editedValues.number_of_users
                        )
                      }
                    />
                    <FontAwesomeIcon
                      icon={faTimes}
                      className="icon-cancel"
                      onClick={() => {
                        setEditing({ planId: null, field: null });
                        setFieldError({ planId: null, field: null, message: "" });
                      }}
                    />
                  </div>
                ) : (
                  <div className="value-row">
                    {row.number_of_users}
                    <FontAwesomeIcon
                      icon={faEdit}
                      className="icon-edit"
                      onClick={() => startEditingField(row, "number_of_users")}
                    />
                  </div>
                )}
              </div>

              {/* ------------------- STORAGE ------------------- */}
              <div className="override-field">
                <label>Storage (MB)</label>

                {isEditingField(row, "storage_mb") ? (
                  <div className="edit-row">
                    <input
                      type="number"
                      value={editedValues.storage_mb}



                      onChange={(e) => {
                        setEditedValues({
                          ...editedValues,
                          storage_mb: e.target.value,
                        });

                        setFieldError({ planId: null, field: null, message: "" });
                      }}


                    />
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="icon-save"
                      onClick={() =>
                        saveFieldToBackend(
                          "storage_mb",
                          editedValues.storage_mb
                        )
                      }
                    />
                    <FontAwesomeIcon
                      icon={faTimes}
                      className="icon-cancel"
                      onClick={() => {
                        setEditing({ planId: null, field: null });
                        setFieldError({ planId: null, field: null, message: "" });
                      }}
                    />
                  </div>
                ) : (
                  <div className="value-row">
                    {row.storage_mb}
                    <FontAwesomeIcon
                      icon={faEdit}
                      className="icon-edit"
                      onClick={() => startEditingField(row, "storage_mb")}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
